import { useState } from "react";
import { listAccountConfigs } from "../api/config";
import { exportUsers } from "../api/users";
import type { ExportUserItemDto } from "../api/types";
import { useAsync } from "../hooks/useAsync";
import { formatBeijingTime } from "../utils/datetime";

const TIERS = ["", "pro", "proplus", "promax", "power"];
const SUBSCRIPTION_STATUSES = ["", "PENDING", "ACTIVE", "CANCELED", "NEEDS_MANUAL"];
const ACTIVATION_STATUSES = [
  "",
  "PENDING_INVITE",
  "AWAITING_VERIFICATION_CODE",
  "ACTIVE",
  "MFA_REQUIRED",
  "NEEDS_MANUAL",
  "SUSPICIOUS_REJECTED",
];

/** 目标账号下拉框"全部"选项对应的取值,后端 export_users_handler 识别为跨账号导出。 */
const ALL_ACCOUNTS = "ALL";

/**
 * 用户导出页(新增页面):按目标账号导出邮箱/用户名/层级/密码,
 * 支持按创建日期区间/邮箱/用户名/层级筛选。仅 Admin 可见入口
 * (对应后端 export_users_handler 的 ADMIN_ONLY 限制,§6.3 密码相关权限)。
 */
export function ExportUsersPage() {
  const accounts = useAsync(() => listAccountConfigs(), []);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [tier, setTier] = useState("");
  const [subscriptionStatus, setSubscriptionStatus] = useState("");
  const [activationStatus, setActivationStatus] = useState("");
  const [expiryFrom, setExpiryFrom] = useState("");
  const [expiryTo, setExpiryTo] = useState("");
  const [inUse, setInUse] = useState("");

  const [results, setResults] = useState<ExportUserItemDto[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const accountId = selectedAccount || accounts.data?.accounts[0]?.account_id || "";

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accountId) {
      setError("请先选择目标账号");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const resp = await exportUsers({
        accountId,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        email: email || undefined,
        username: username || undefined,
        tier: tier || undefined,
        subscriptionStatus: subscriptionStatus || undefined,
        activationStatus: activationStatus || undefined,
        expiryFrom: expiryFrom || undefined,
        expiryTo: expiryTo || undefined,
        inUse: inUse || undefined,
      });
      setResults(resp.users);
    } catch (err) {
      setError(err instanceof Error ? err.message : "查询失败");
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCsv = () => {
    if (!results || results.length === 0) return;
    const baseColumns = [
      "email",
      "username",
      "tier",
      "password",
      "created_at",
      "subscription_status",
      "activation_status",
      "subscription_end_at",
      "in_use",
    ];
    const header = accountId === ALL_ACCOUNTS ? ["account_id", ...baseColumns] : baseColumns;
    const escapeCsvCell = (value: unknown) => {
      const str = value === null || value === undefined ? "" : String(value);
      // 字段里出现逗号/引号/换行时需要用双引号包裹,内部的双引号要转义成两个双引号
      if (/[",\n]/.test(str)) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };
    const lines = [
      header.join(","),
      ...results.map((r) =>
        header
          .map((key) =>
            key === "created_at"
              ? escapeCsvCell(formatBeijingTime(r.created_at))
              : escapeCsvCell(r[key as keyof ExportUserItemDto])
          )
          .join(",")
      ),
    ];
    // 加 UTF-8 BOM,避免 Excel 打开中文/特殊字符时乱码
    const csvContent = "\uFEFF" + lines.join("\r\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    link.href = url;
    link.download = `kiro-users-${accountId}-${timestamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  /**
   * 下载 TXT(与"下载 CSV"并列,不影响原有筛选/结果展示逻辑)。
   *
   * 格式(按用户确认的示例,单账号 3 个用户为例):
   *   AWS ID:070215089480
   *                            <- 空行
   *   用户名:jacob.collins
   *   密码:HMUxb!iJ%3iRXfd5    <- 用户名与密码紧邻,中间不留空行
   *                            <- 空行(仅用于分隔相邻两个用户)
   *   用户名:jessica.collins
   *   密码:hnRTf*4Xb1%7CPUQ
   *
   * 要点:
   *   - 三种前缀统一用**半角**冒号:"AWS ID:" / "用户名:" / "密码:"
   *     (v2.29 定稿时 "AWS ID" 曾用全角冒号,后按运营要求统一改为半角)
   *   - 最后一个用户的密码行之后不追加空行
   *   - 目标账号选"全部"时按 account_id 分组,每组一个 AWS ID 块,
   *     块与块之间同样是单个空行
   */
  const handleDownloadTxt = () => {
    if (!results || results.length === 0) return;

    const groups = new Map<string, ExportUserItemDto[]>();
    for (const r of results) {
      const acctId = accountId === ALL_ACCOUNTS ? r.account_id || "" : accountId;
      const list = groups.get(acctId);
      if (list) {
        list.push(r);
      } else {
        groups.set(acctId, [r]);
      }
    }

    const blocks: string[] = [];
    for (const [acctId, users] of groups) {
      const lines: string[] = [`AWS ID:${acctId}`, ""];
      users.forEach((u, idx) => {
        lines.push(`用户名:${u.username}`);
        lines.push(`密码:${u.password ?? ""}`);
        if (idx < users.length - 1) {
          lines.push("");
        }
      });
      blocks.push(lines.join("\r\n"));
    }

    const txtContent = "\uFEFF" + blocks.join("\r\n\r\n");
    const blob = new Blob([txtContent], { type: "text/plain;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    link.href = url;
    link.download = `kiro-users-${accountId}-${timestamp}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <h2 className="section-title">导出用户</h2>

      <div className="card">
        <form onSubmit={handleSearch}>
          <div className="form-row">
            <label>目标账号</label>
            <select value={accountId} onChange={(e) => setSelectedAccount(e.target.value)}>
              <option value={ALL_ACCOUNTS}>全部</option>
              {accounts.data?.accounts.map((a) => (
                <option key={a.account_id} value={a.account_id}>
                  {a.account_id}
                </option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <label>创建日期从</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            <label style={{ marginLeft: 12 }}>到</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
          <div className="form-row">
            <label>邮箱包含</label>
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="子串匹配,不区分大小写" />
          </div>
          <div className="form-row">
            <label>用户名包含</label>
            <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="子串匹配,不区分大小写" />
          </div>
          <div className="form-row">
            <label>层级</label>
            <select value={tier} onChange={(e) => setTier(e.target.value)}>
              {TIERS.map((t) => (
                <option key={t} value={t}>
                  {t || "(全部)"}
                </option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <label>订阅状态</label>
            <select value={subscriptionStatus} onChange={(e) => setSubscriptionStatus(e.target.value)}>
              {SUBSCRIPTION_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s || "(全部)"}
                </option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <label>激活状态</label>
            <select value={activationStatus} onChange={(e) => setActivationStatus(e.target.value)}>
              {ACTIVATION_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s || "(全部)"}
                </option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <label>到期日从</label>
            <input type="date" value={expiryFrom} onChange={(e) => setExpiryFrom(e.target.value)} />
            <label style={{ marginLeft: 12 }}>到</label>
            <input type="date" value={expiryTo} onChange={(e) => setExpiryTo(e.target.value)} />
          </div>
          <div className="form-row">
            <label>使用中</label>
            <select value={inUse} onChange={(e) => setInUse(e.target.value)}>
              <option value="">(全部)</option>
              <option value="true">是</option>
              <option value="false">否</option>
            </select>
          </div>
          <button className="primary" type="submit" disabled={loading}>
            {loading ? "查询中..." : "查询"}
          </button>
        </form>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {results && (
        <div className="card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <p style={{ margin: 0 }}>共 {results.length} 条记录</p>
            <div>
              <button className="primary" onClick={handleDownloadCsv} disabled={results.length === 0}>
                下载 CSV
              </button>
              <button
                className="primary"
                style={{ marginLeft: 8 }}
                onClick={handleDownloadTxt}
                disabled={results.length === 0}
              >
                下载 TXT
              </button>
            </div>
          </div>
          <table>
            <thead>
              <tr>
                {accountId === ALL_ACCOUNTS && <th>账号 ID</th>}
                <th>邮箱</th>
                <th>用户名</th>
                <th>层级</th>
                <th>密码</th>
                <th>创建时间</th>
                <th>订阅状态</th>
                <th>激活状态</th>
                <th>到期日</th>
                <th>使用中</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r) => (
                <tr key={`${r.account_id}#${r.email}`}>
                  {accountId === ALL_ACCOUNTS && <td>{r.account_id}</td>}
                  <td>{r.email}</td>
                  <td>{r.username}</td>
                  <td>{r.tier}</td>
                  <td style={{ fontFamily: "monospace" }}>{r.password}</td>
                  <td>{formatBeijingTime(r.created_at)}</td>
                  <td>{r.subscription_status ?? "-"}</td>
                  <td>{r.activation_status ?? "-"}</td>
                  <td>{r.subscription_end_at ?? "-"}</td>
                  <td>{r.in_use ? "是" : "否"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {results.length === 0 && <p style={{ color: "#64748b" }}>没有匹配的记录</p>}
        </div>
      )}
    </div>
  );
}
